/** @jsxImportSource @emotion/react */

import { useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import BaseButton from "components/common/Button/Base";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import SelectBusinessDialog, {
  MatchCandidateBusiness,
} from "../SelectBusiness";
import {
  PaymentNotice,
  PaymentNoticeDocType,
  PAYMENT_NOTICE_DOC_TYPE_LABELS,
  PAYMENT_NOTICE_MATCH_STATUS_LABELS,
} from "models/paymentNotice.model";
import { deletePaymentNotice } from "pages/remotes";
import { PALETTE } from "styles/base/variables";
import { convertCurrency, convertDate, convertRegNumber } from "utils/converter";

interface PaymentNoticeDetailDialogProps {
  isOpen: boolean;
  paymentNotice: PaymentNotice | null;
  organizationId: string | null;
  // 매칭 변경 팝업에서 후보로 보여줄 사업자 목록 (현재 매칭된 사업자는 제외하고 노출됨)
  businessList: MatchCandidateBusiness[];
  onClose: () => void;
  onDeleted: (paymentNoticeId: string) => void;
  // 매칭 사업자가 변경되면, 이 납부서는 더 이상 현재 사업자 소속이 아니므로
  // 상위 화면(사업자별 상세 목록)에서 제거하고 전체 현황도 갱신해야 한다.
  onRematched: (paymentNoticeId: string) => void;
}

function PaymentNoticeDetailDialog({
  isOpen,
  paymentNotice,
  organizationId,
  businessList,
  onClose,
  onDeleted,
  onRematched,
}: PaymentNoticeDetailDialogProps) {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSelectBusinessOpen, setIsSelectBusinessOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });

  if (!paymentNotice) return null;

  // 원천세 신고서 접수증(docType: TAX_RECEIPT)에만 존재하는 상세 항목은
  // taxReceipt 중첩 객체로 내려오며, 접수증일 때만 아래 rows에 추가로 노출한다.
  const taxReceipt =
    paymentNotice.docType === "TAX_RECEIPT" ? paymentNotice.taxReceipt : null;
  const formatAmount = (value: number | null | undefined) =>
    value === null || value === undefined
      ? "-"
      : `${convertCurrency(value)}원`;

  const downloadFile = () => {
    if (!paymentNotice.downloadUrl) return;
    window.open(paymentNotice.downloadUrl, "_blank");
  };

  const confirmDelete = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await deletePaymentNotice(paymentNotice.id);
      setIsDeleteConfirmOpen(false);
      setIsDeleting(false);
      onDeleted(paymentNotice.id);
      onClose();
    } catch (err: any) {
      setIsDeleting(false);
      setAlertDialogData({
        message:
          err.response?.data?.message || "납부서 삭제 중 오류가 발생했습니다.",
        type: "error",
      });
      setIsAlertOpen(true);
    }
  };

  const rows: { label: string; value: string }[] = [
    { label: "매칭 상태", value: PAYMENT_NOTICE_MATCH_STATUS_LABELS[paymentNotice.matchStatus] },
    {
      label: "문서 종류",
      value: paymentNotice.docType
        ? PAYMENT_NOTICE_DOC_TYPE_LABELS[
            paymentNotice.docType as PaymentNoticeDocType
          ] || paymentNotice.docType
        : "-",
    },
    { label: "상호", value: paymentNotice.companyName || "-" },
    {
      label: "사업자번호",
      value: paymentNotice.businessNo
        ? convertRegNumber(paymentNotice.businessNo)
        : "-",
    },
    { label: "세목", value: paymentNotice.taxItem || "-" },
    { label: "소득 구분", value: paymentNotice.incomeType || "-" },
    { label: "귀속기간", value: paymentNotice.period || "-" },
    {
      label: "납부기한",
      value: paymentNotice.dueDate ? convertDate(paymentNotice.dueDate) : "-",
    },
    // 접수증은 "금액"이 "소득세 등 납부세액"과 동일 값이라 금액 행 대신
    // 접수증 전용 항목만 노출한다.
    ...(taxReceipt
      ? [
          {
            label: "소득세 등 납부세액",
            value: formatAmount(taxReceipt.incomeTaxAmount),
          },
          {
            label: "농어촌특별세 납부세액",
            value: formatAmount(taxReceipt.ruralTaxAmount),
          },
          {
            label: "차월이월환급세액",
            value: formatAmount(taxReceipt.carriedOverRefund),
          },
          {
            label: "환급신청금액",
            value: formatAmount(taxReceipt.refundRequestAmount),
          },
        ]
      : [{ label: "금액", value: `${convertCurrency(paymentNotice.amount)}원` }]),
    { label: "전자납부번호", value: paymentNotice.epayNo || "-" },
    {
      label: "계좌",
      value:
        paymentNotice.accounts?.length > 0
          ? paymentNotice.accounts
              .map((account) => `${account.bank} ${account.no}`)
              .join(", ")
          : "-",
    },
    { label: "업로드일", value: convertDate(paymentNotice.createdDate, "YYYY.MM.DD") },
  ];

  return (
    <>
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{paymentNotice.originalFileName}</DialogTitle>
      <DialogContent>
        <div
          css={css`
            min-width: 400px;
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          {(paymentNotice.matchNote || paymentNotice.confidenceReason) && (
            <div
              css={css`
                padding: 10px 12px;
                border-radius: 8px;
                background-color: ${PALETTE.background};
                font-size: 13px;
                color: ${PALETTE.textMid};
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              {paymentNotice.matchNote && <span>{paymentNotice.matchNote}</span>}
              {paymentNotice.confidenceReason && (
                <span>{paymentNotice.confidenceReason}</span>
              )}
            </div>
          )}

          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            {rows.map((row) => (
              <div
                key={row.label}
                css={css`
                  padding: 10px 0;
                  display: flex;
                  align-items: flex-start;
                  justify-content: space-between;
                  gap: 16px;
                  border-bottom: 1px solid ${PALETTE.borderLight};

                  &:last-child {
                    border-bottom: none;
                  }
                `}
              >
                <span
                  css={css`
                    flex: none;
                    font-size: 14px;
                    color: ${PALETTE.textMid};
                  `}
                >
                  {row.label}
                </span>
                <span
                  css={css`
                    font-size: 14px;
                    color: ${PALETTE.text};
                    text-align: right;
                    word-break: break-all;
                  `}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
      <div
        css={css`
          margin: 6px 24px;
          border-top: 1px solid ${PALETTE.border};
        `}
      />
      <DialogActions
        css={css`
          padding: 12px 24px 20px 24px;
          display: flex;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 8px;
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.error}
            disabled={!organizationId}
            onClick={() => setIsDeleteConfirmOpen(true)}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <DeleteOutlineIcon fontSize="small" />
              삭제
            </span>
          </BaseButton>
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            disabled={!organizationId}
            onClick={() => setIsSelectBusinessOpen(true)}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <SyncAltIcon fontSize="small" />
              매칭 변경
            </span>
          </BaseButton>
        </div>
        <div
          css={css`
            display: flex;
            gap: 8px;
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={onClose}
          >
            닫기
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.primary}
            disabled={!paymentNotice.downloadUrl}
            onClick={downloadFile}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <FileDownloadOutlinedIcon fontSize="small" />
              다운로드
            </span>
          </BaseButton>
        </div>
      </DialogActions>
    </Dialog>
    <Dialog
      open={isDeleteConfirmOpen}
      onClose={() => !isDeleting && setIsDeleteConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>알림</DialogTitle>
      <DialogContent>
        <DialogContentText>이 납부서를 삭제하시겠습니까?</DialogContentText>
        <DialogContentText>삭제한 데이터는 복구할 수 없습니다.</DialogContentText>
      </DialogContent>
      <DialogActions
        css={css`
          margin: 0 12px;
          padding: 12px;
          border-top: 1px solid ${PALETTE.border};
        `}
      >
        <BaseButton
          variant="outlined"
          color={PALETTE.primary}
          disabled={isDeleting}
          onClick={() => setIsDeleteConfirmOpen(false)}
        >
          취소
        </BaseButton>
        <BaseButton
          variant="contained"
          color={PALETTE.error}
          disabled={isDeleting}
          onClick={confirmDelete}
        >
          {isDeleting ? "삭제 중" : "삭제"}
        </BaseButton>
      </DialogActions>
    </Dialog>
    <SelectBusinessDialog
      isOpen={isSelectBusinessOpen}
      organizationId={organizationId}
      paymentNoticeId={paymentNotice.id}
      excludeBusinessId={paymentNotice.businessId}
      businessList={businessList}
      onClose={() => setIsSelectBusinessOpen(false)}
      onSuccess={() => {
        setIsSelectBusinessOpen(false);
        onRematched(paymentNotice.id);
        onClose();
      }}
    />
    <AlertDialog
      isOpen={isAlertOpen}
      setIsOpen={setIsAlertOpen}
      alertData={alertDialogData}
    />
    </>
  );
}

export default PaymentNoticeDetailDialog;
